<template>
    <div class="text-widget">
        <div class="widget-header">
            <div class="left">
                <i
                    class="material-icons drag-handle"
                    v-tippy="{
                        placement: 'top',
                        theme: 'light',
                        arrow: true,
                        animation: 'scale-extreme',
                        duration: [200, 200],
                        hideOnClick: true,
                    }"
                    content="Sürükleyerek taşıyabilirsiniz"
                    >drag_indicator</i
                >
                <widget-title :widget="widget" :table="table" @widget-updated="handleWidgetUpdated" />
            </div>

            <widget-actions
                @fullscreen="handleFullscreen"
                @edit="handleEdit"
                @rename="handleRename"
                @duplicate="handleDuplicate"
                @dock="handleDock"
                @export="handleExport"
                @delete="$emit('delete', widget._id)"
                :title="widget.title"
                :widget="widget"
                :table="table"
                @widget-updated="handleWidgetUpdated"
                @widget-duplicated="handleWidgetDuplicated"
                @widget-deleted="handleWidgetDeleted"
            />
        </div>
        <div 
            class="widget-content" 
            :style="{
                backgroundColor: $_.get(widget, 'settings.backgroundColor', '#ffffff'),
                padding: ($_.get(widget, 'settings.padding', 16)) + 'px',
                textAlign: $_.get(widget, 'settings.textAlign', 'left')
            }"
        >
            <empty-widget
                v-if="!hasValidSettings"
                :error="null"
                :has-valid-settings="hasValidSettings"
                @edit="handleEdit"
            />
            <div v-else class="text-content" v-html="sanitizedContent"></div>
        </div>

        <text-settings-modal
            v-if="showSettingsModal"
            :visible="showSettingsModal"
            :widget="widget"
            @close="showSettingsModal = false"
            @save="handleSettingsSave"
            :table="table"
        />
    </div>
</template>

<script>
import Vue from "vue";
import { TABLE_API } from "../../../../../../api/table";
import log2 from "../../../../../../utils/log2";
import WidgetActions from "../charts/components/WidgetActions.vue";
import WidgetTitle from "../charts/components/WidgetTitle.vue";
import EmptyWidget from "../EmptyWidget.vue";
import TextSettingsModal from "./TextSettingsModal.vue";
import DOMPurify from "dompurify";

const log = log2("TextWidget");

export default Vue.extend({
    name: "TextWidget",
    components: {
        WidgetActions,
        WidgetTitle,
        EmptyWidget,
        TextSettingsModal,
    },
    props: {
        widget: {
            type: Object,
            required: true,
        },
        table: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            showSettingsModal: false,
        };
    },
    computed: {
        hasValidSettings() {
            return !!this.$_.get(this.widget, 'settings') && !!this.$_.get(this.widget, 'settings.content');
        },
        sanitizedContent() {
            const content = this.$_.get(this.widget, 'settings.content', '');
            return DOMPurify.sanitize(content, {
                ADD_TAGS: ["iframe"],
                ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling"],
            });
        },
    },
    methods: {
        handleFullscreen() {
            // TODO: Tam ekran modunu implement et
        },
        async handleRename(newName) {
            try {
                await TABLE_API.updateWidget(this.table._id, this.widget.page, this.widget._id, {
                    title: newName,
                });
                this.$emit("widget-updated", {
                    ...this.widget,
                    title: newName,
                });
            } catch (error) {
                console.error("Widget adı güncellenirken hata:", error);
                throw error;
            }
        },
        async handleDuplicate() {
            try {
                const result = await TABLE_API.duplicateWidget(this.table._id, this.widget.page, this.widget._id);
                this.$message.success("Widget başarıyla çoğaltıldı");
                this.$emit("widget-duplicated", {
                    widget: result.widget,
                    page: result.page,
                });
            } catch (error) {
                console.error("Widget çoğaltılırken hata:", error);
                this.$message.error("Widget çoğaltılırken bir hata oluştu");
            }
        },
        handleDock() {
            // TODO: Sabitleme işlemini implement et
        },
        handleExport() {
            // TODO: Dışa aktarma işlemini implement et
        },
        handleEdit() {
            this.showSettingsModal = true;
        },
        async handleSettingsSave(newSettings) {
            try {
                await TABLE_API.updateWidget(this.table._id, this.widget.page, this.widget._id, {
                    settings: newSettings,
                });
                this.$message.success("Widget ayarları güncellendi");
                this.$emit("widget-updated", {
                    ...this.widget,
                    settings: newSettings,
                });
                this.showSettingsModal = false;
            } catch (error) {
                console.error("Widget ayarları güncellenirken hata:", error);
                this.$message.error("Widget ayarları güncellenirken bir hata oluştu");
            }
        },
        async handleWidgetUpdated(updatedWidget) {
            this.$emit("widget-updated", updatedWidget);
        },
        async handleWidgetDuplicated(result) {
            this.$emit("widget-duplicated", result);
        },
        async handleWidgetDeleted(widgetId) {
            this.$emit("delete", widgetId);
        },
    },
});
</script>

<style scoped lang="scss">
.text-widget {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 4px;
    overflow: hidden;
    position: relative;
    border: 2px solid #fafafa;
    transition: border-color 0.2s ease;

    &:hover {
        border-color: #1890ff;

        .drag-handle {
            visibility: visible;
            line-height: 1px;
        }
    }

    .drag-handle {
        transform: translateY(-50%);
        font-size: 20px;
        color: rgba(0, 0, 0, 0.25);
        cursor: move;
        transition: opacity 0.2s, color 0.2s, left 0.2s;
        visibility: hidden;
        line-height: 1px;
        &:hover {
            color: rgba(0, 0, 0, 0.45);
            left: -8px;
        }
    }

    .widget-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 16px;
        border-bottom: 1px solid #e8e8e8;
        background: #fafafa;
        .left {
            display: flex;
            align-items: center;
            gap: 5px;
            width: -webkit-fill-available;
        }
    }

    .widget-content {
        flex: 1;
        min-height: 0;
        position: relative;
        overflow: auto;

        .text-content {
            :deep(p) {
                margin-bottom: 1em;
                &:last-child {
                    margin-bottom: 0;
                }
            }

            :deep(h1), :deep(h2), :deep(h3), :deep(h4), :deep(h5), :deep(h6) {
                margin: 1em 0 0.5em;
                &:first-child {
                    margin-top: 0;
                }
            }

            :deep(ul), :deep(ol) {
                padding-left: 1.5em;
                margin-bottom: 1em;
            }

            :deep(blockquote) {
                border-left: 4px solid #e8e8e8;
                padding-left: 1em;
                margin: 1em 0;
                color: rgba(0, 0, 0, 0.65);
            }

            :deep(pre) {
                background: #f5f5f5;
                padding: 1em;
                border-radius: 4px;
                overflow-x: auto;
                margin: 1em 0;
            }

            :deep(code) {
                background: #f5f5f5;
                padding: 0.2em 0.4em;
                border-radius: 3px;
                font-size: 0.9em;
            }

            :deep(table) {
                width: 100%;
                border-collapse: collapse;
                margin: 1em 0;

                th, td {
                    border: 1px solid #e8e8e8;
                    padding: 0.5em;
                }

                th {
                    background: #fafafa;
                }
            }

            :deep(img) {
                max-width: 100%;
                height: auto;
            }

            :deep(a) {
                color: #1890ff;
                text-decoration: none;

                &:hover {
                    text-decoration: underline;
                }
            }
        }
    }
}
</style> 